/*
 * Ecranul 3: cosul si trimiterea cererii.
 *
 * Cosul se citeste mereu de la api, niciodata din sessionStorage. Contractul
 * spune ca e al utilizatorului si al companiei din token, nu al sesiunii: o
 * copie locala ar arata alte articole decat cele care pleaca efectiv la
 * `POST /shop/cart/submit`, si diferenta s-ar vedea abia in cererea aprobata.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as shop from '../api/shop';
import type { CartItem } from '../api/types';
import { useCounts } from '../components/CountsContext';
import { useToasts } from '../components/ToastContext';
import { EmptyState, ErrorState, InlineAlert, Loading } from '../components/states';
import { EM_DASH, formatMoney, plural } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { t } from '../lib/i18n';

export function CartScreen() {
  const cart = useAsync((signal) => shop.cart({ signal }), []);
  const { refreshCart } = useCounts();
  const { push } = useToasts();
  const navigate = useNavigate();

  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [noteMissing, setNoteMissing] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [priority, setPriority] = useState('');
  const [busyQtyId, setBusyQtyId] = useState<number | null>(null);

  /**
   * Alta cantitate pe un rand.
   *
   * Se cere serverului si se reincarca cosul din raspunsul lui, nu se socoteste
   * local: pretul randului si totalul sunt bani, iar banii se aduna pe server -
   * aici sunt siruri zecimale, si adunarea lor in JS ar fi exact float-ul pe care
   * contractul il interzice.
   *
   * Sub unu nu se coboara: acolo raspunsul nu e "zero bucati", e "scoate-l din
   * cos", si aia are butonul ei.
   */
  const changeQuantity = async (item: CartItem, next: number) => {
    if (next < 1 || next > 99 || next === (item.quantity ?? 1)) {
      return;
    }

    setBusyQtyId(item.id);
    try {
      await shop.setCartItemQuantity(item.id, next);
      refreshCart();
      cart.reload();
    } catch (cause) {
      push(cause instanceof Error ? cause.message : t('Could not change the quantity.'), 'error');
    } finally {
      setBusyQtyId(null);
    }
  };

  const remove = async (item: CartItem) => {
    setRemovingId(item.id);
    try {
      await shop.removeCartItem(item.id);
      push(t('Item removed.'));
      refreshCart();
      cart.reload();
    } catch (cause) {
      push(cause instanceof Error ? cause.message : t('Could not remove the item.'), 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    /* Oprita aici, ca omul sa vada mesajul langa campul care il priveste, nu ca
       eroare generala de la api. Api-ul o verifica oricum - vezi
       ServiceRequests::submit() - asta e doar drumul scurt. */
    if (note.trim() === '') {
      setNoteMissing(true);
      return;
    }

    setSubmitting(true);
    try {
      const result = await shop.submitCart(note.trim(), priority ? Number(priority) : undefined);
      refreshCart();
      if (result.request_id) {
        push(`Request #${result.request_id} submitted.`);
        void navigate(`/requests/${result.request_id}`);
      } else {
        /* Contractul cere `request_id` inapoi. Daca lipseste, se merge la lista
           in loc sa se navigheze catre o adresa cu "undefined" in ea. */
        push(t('Request submitted.'));
        void navigate('/requests');
      }
    } catch (cause) {
      setSubmitError(cause instanceof Error ? cause.message : t('The request could not be submitted.'));
      setSubmitting(false);
    }
  };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{t('Cart')}</p>
          <h1 className="page-title">{t('Ready to submit')}</h1>
          <p className="page-sub">{t('Review what you are about to request, then submit it.')}</p>
        </div>
      </header>

      {cart.status === 'loading' ? <Loading label={t('Loading your cart…')} /> : null}
      {cart.status === 'error' && cart.error ? (
        <ErrorState error={cart.error} onRetry={cart.reload} />
      ) : null}

      {cart.status === 'ready' && cart.data ? (
        cart.data.items.length === 0 ? (
          <EmptyState
            title={t('Your cart is empty')}
            text={t('Add services from the catalog and they will appear here.')}
            action={
              <Link className="btn btn--primary" to="/catalog">{t('Browse the catalog')}</Link>
            }
          />
        ) : (
          <div className="split">
            <div className="split__main">
              <section className="panel panel--flush">
                <div className="panel__head">
                  <h2 className="panel__title">
                    {cart.data.items.length}{' '}
                    {plural(cart.data.items.length, 'item', 'items')}
                  </h2>
                </div>
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('Service')}</th>
                        <th scope="col">{t('Note')}</th>
                        <th scope="col" className="num-col">{t('Qty')}</th>
                        <th scope="col" className="num-col">{t('Cost')}</th>
                        <th scope="col">
                          <span className="sr-only">{t('Actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.data.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <span className="table__strong">
                              {item.name ?? item.catalog?.name ?? `Catalog item #${item.catalog_id ?? item.id}`}
                            </span>
                            {item.category ? (
                              <span className="table__sub">{item.category.name}</span>
                            ) : null}
                          </td>
                          <td className="table__muted">{item.note || EM_DASH}</td>
                          <td className="num-col">
                            {/* Sagetile, nu o casuta de scris: in cos se creste sau
                                se scade cu unu, si o casuta ar fi cerut si o apasare
                                de confirmare pentru acelasi lucru. */}
                            <span className="qty">
                              <button
                                type="button"
                                className="qty__step"
                                aria-label={t('One less')}
                                disabled={busyQtyId === item.id || (item.quantity ?? 1) <= 1}
                                onClick={() => void changeQuantity(item, (item.quantity ?? 1) - 1)}
                              >
                                &minus;
                              </button>
                              <span className="qty__value num">{item.quantity ?? 1}</span>
                              <button
                                type="button"
                                className="qty__step"
                                aria-label={t('One more')}
                                disabled={busyQtyId === item.id || (item.quantity ?? 1) >= 99}
                                onClick={() => void changeQuantity(item, (item.quantity ?? 1) + 1)}
                              >
                                +
                              </button>
                            </span>
                          </td>
                          <td className="num-col num">{formatMoney(item.cost)}</td>
                          <td className="num-col">
                            <button
                              type="button"
                              className="btn btn--sm btn--quiet-danger"
                              disabled={removingId === item.id}
                              onClick={() => void remove(item)}
                            >
                              {removingId === item.id ? t('Removing…') : t('Remove')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <aside className="split__side">
              <div className="panel">
                <h2 className="panel__title">{t('Submit request')}</h2>

                {/* Totalul se afiseaza doar daca api-ul il trimite. Nu se aduna
                    local: banii sunt siruri zecimale, iar adunarea lor in JS ar
                    insemna exact float-ul pe care contractul il interzice. */}
                {cart.data.total ? (
                  <dl className="facts">
                    <dt className="micro-label">{t('Total')}</dt>
                    <dd className="facts__strong num">{formatMoney(cart.data.total)}</dd>
                  </dl>
                ) : null}

                {/* Spus la trimitere, nu doar dupa: omul care apasa aici e
                    acelasi care va cauta aprobarea peste doua zile. Daca afla
                    abia atunci ca s-a mutat, o cauta intai in magazin. */}
                <p className="muted">
            {t('Submitting creates one request and a ticket for each item. Anything that needs approval is approved on its ticket, not in the shop.')}
          </p>

                <form className="side-form" onSubmit={submit}>
                  {submitError ? <InlineAlert>{submitError}</InlineAlert> : null}

                  {/*
                      Prioritatea, aceeasi lista ca in Easeedesk.

                      Una pe cerere, nu pe articol: o cerere pleaca dintr-o singura
                      apasare, cu o singura justificare, si cine cere trei servicii
                      pentru ca i s-a stricat statia le vrea pe toate la fel de
                      repede. O casuta pe fiecare rand ar fi aceeasi intrebare pusa
                      de cinci ori.

                      Alegerea de start lasa hotararea la service desk: cine nu stie
                      ce sa aleaga nu trebuie sa aleaga, si atunci tichetele iau
                      prioritatea implicita a furnizorului.
                  */}
                  {cart.data.priorities && cart.data.priorities.length > 0 ? (
                    <div className="field">
                      <label className="field__label" htmlFor="cart-priority">
                        {t('Priority')}
                      </label>
                      <select
                        id="cart-priority"
                        className="field__control"
                        value={priority}
                        onChange={(event) => setPriority(event.target.value)}
                      >
                        <option value="">{t('Leave it to the service desk')}</option>
                        {cart.data.priorities.map((one) => (
                          <option key={one.id} value={String(one.id)}>
                            {one.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  {/*
                      Justificarea, obligatorie.
                      Ea ajunge in descrierea tichetului de cerere, adica in
                      chiar textul pe care il citeste cel care decide. Un tichet
                      fara ea il pune sa intrebe inapoi de ce s-a cerut, si
                      cererea sta pana raspunde cineva.
                  */}
                  <div className="field">
                    <label className="field__label" htmlFor="cart-note">{t('Justification')}<span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="cart-note"
                      className="field__control"
                      rows={4}
                      required
                      aria-required="true"
                      aria-invalid={noteMissing || undefined}
                      aria-describedby="cart-note-help"
                      placeholder={t('Why you need this.')}
                      value={note}
                      onChange={(event) => {
                        setNote(event.target.value);
                        if (noteMissing) setNoteMissing(false);
                      }}
                    />
                    <p className="field__hint" id="cart-note-help">{t('Goes on the ticket, for whoever decides.')}</p>
                    {noteMissing ? (
                      <p className="field__hint field__hint--error" role="alert">{t('Write why you need this.')}</p>
                    ) : null}
                  </div>

                  <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
                    {submitting ? t('Submitting…') : t('Submit request')}
                  </button>
                </form>
              </div>
            </aside>
          </div>
        )
      ) : null}
    </>
  );
}
